import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Theme } from '@app/theme';
import { Invoice } from '@app/api';
import { toEuros } from 'lib/numbers';
import {
  InvoiceStatusChip,
  ProductLine,
  ProductLinesContainer,
  SubTotal,
  TextLoadingPlaceholder,
} from 'components';

const useStyles = createUseStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase * 2,
    marginBottom: theme.marginBase,
  },
  title: {
    ...theme.fonts.h3,
  },
  dates: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase / 2,
    marginBottom: theme.marginBase * 3,
  },
  overdue: {
    color: theme.colors.redAccessible,
  },
  hr: {
    marginTop: theme.marginBase,
    marginBottom: theme.marginBase * 2,
    width: '100%',
    borderBottom: {
      style: 'solid',
      width: 1,
      color: theme.colors.grayBorder,
    },
  },
  total: {
    marginTop: theme.marginBase * 2,
    display: 'flex',
    alignItems: 'center',
    ...theme.fonts.h4,
  },
  label: {
    flex: 1,
  },
  price: {
    textAlign: 'right',
  },
}));

interface Props {
  invoice?: Invoice;
}

export const InvoiceDetails = ({ invoice }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isOverdue = !!invoice?.dueDate && moment(invoice.dueDate).isBefore();
  const productLines = invoice?.productLines || [];

  return (
    <div>
      <div className={classes.header}>
        <h1 className={classes.title}>
          {invoice ? (
            t('billing.payInvoice.invoiceTitle', { name: invoice.name || '' })
          ) : (
            <TextLoadingPlaceholder
              sample={t('billing.payInvoice.invoiceTitle', { name: '000000' })}
            />
          )}
        </h1>
        {!!invoice && <InvoiceStatusChip status={invoice.status} />}
      </div>

      <div className={classes.dates}>
        {!invoice ? (
          <TextLoadingPlaceholder
            sample={t('billing.payInvoice.dueDate', {
              date: moment().format(),
            })}
          />
        ) : invoice.dueDate ? (
          <div className={isOverdue ? classes.overdue : undefined}>
            {t('billing.payInvoice.dueDate', { date: invoice.dueDate })}
          </div>
        ) : (
          !!invoice.openAt && (
            <div>
              {t('billing.payInvoice.issuedAt', { date: invoice.openAt })}
            </div>
          )
        )}
      </div>

      {!!productLines.length && (
        <ProductLinesContainer>
          {productLines.map((line) => (
            <ProductLine
              key={line.id}
              label={line.title || ''}
              quantity={line.quantity}
              price={line.total}
              strong
            />
          ))}
        </ProductLinesContainer>
      )}

      <div className={classes.hr} />

      {invoice && invoice.totalHT !== undefined && (
        <SubTotal totalExclTaxes={invoice.totalHT} taxes={invoice.tax} />
      )}

      <div className={classes.total}>
        <div className={classes.label}>{t('billing.payInvoice.total')}</div>
        <div className={classes.price}>
          {invoice ? (
            toEuros(invoice.total || 0, true)
          ) : (
            <TextLoadingPlaceholder sample="7 500,00 €" />
          )}
        </div>
      </div>
    </div>
  );
};
